import React, { useState, useLayoutEffect } from "react";
import {
  Box, Typography, Dialog, DialogContent, IconButton,
  Checkbox, Divider, Button,
} from "@mui/material";
import CloseIcon        from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useGetRolesByCompany, useGetUsersByRole } from "../api/reactQueryHook/useRoles";
import LoadingOverlay from "./LoadingOverlay";

// One role row — calls useGetUsersByRole directly so disabled state is derived
// from query data without async callback indirection. Defaults to disabled until
// data confirms the role has members, closing the race-condition window.
const RoleRow: React.FC<{
  role: { role_id: number; role_name: string };
  checked: boolean;
  onToggle: (id: number) => void;
}> = ({ role, checked, onToggle }) => {
  const { data } = useGetUsersByRole(role.role_id);
  const isEmpty  = data ? data.data.length === 0 : false;
  const disabled = !data || isEmpty;

  return (
    <Box>
      <Box
        onClick={() => { if (!disabled) onToggle(role.role_id); }}
        sx={{
          display: "flex", alignItems: "center", gap: 2,
          px: 3, py: 2, cursor: disabled ? "not-allowed" : "pointer",
          opacity: isEmpty ? 0.45 : 1,
          "&:hover": { bgcolor: disabled ? "transparent" : "action.hover" },
          transition: "background-color 0.12s",
        }}
      >
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={() => {}}
          sx={{
            p: 0, color: "text.disabled",
            "&.Mui-checked": { color: "primary.main" },
            "& .MuiSvgIcon-root": { fontSize: "1.6rem" },
          }}
        />
        <Typography sx={{ color: isEmpty ? "text.disabled" : "text.primary", fontSize: "0.95rem", fontWeight: 400 }}>
          {role.role_name}{isEmpty ? " (no members)" : ""}
        </Typography>
      </Box>
      <Divider sx={{ borderColor: "divider" }} />
    </Box>
  );
};

type Tab = "people" | "roles";

interface AnnounceContentDialogProps {
  open: boolean;
  fileName: string;
  selectedCompanyId: number;
  users: {
    user_id: number;
    first_name: string;
    last_name: string;
    email_id: string;
  }[];
  onClose: () => void;
  onSend: (selectedUserIds: number[], selectedRoleIds: number[]) => void;
  title?: string;
  buttonLabel?: string;
  showWarning?: boolean;
  preSelectedPeople?: number[];
  preSelectedRoles?: number[];
  isLoading?: boolean;
}

const AnnounceContentDialog: React.FC<AnnounceContentDialogProps> = ({
  open,
  fileName,
  selectedCompanyId,
  users,
  onClose,
  onSend,
  title              = "Announce Content to People & Roles",
  buttonLabel        = "Send Announcement",
  showWarning        = true,
  preSelectedPeople  = [],
  preSelectedRoles   = [],
  isLoading          = false,
}) => {
  const [tab,            setTab]           = useState<Tab>("people");
  const [selectedPeople, setSelectedPeople] = useState<Set<number>>(new Set());
  const [selectedRoles,  setSelectedRoles]  = useState<Set<number>>(new Set());
  useLayoutEffect(() => {
    if (open) {
      setSelectedPeople(new Set(preSelectedPeople.map(Number)));
      setSelectedRoles(new Set(preSelectedRoles.map(Number)));
    }
  }, [open, preSelectedPeople.join(","), preSelectedRoles.join(",")]);

  const { data: rolesData, isLoading: rolesLoading } = useGetRolesByCompany(selectedCompanyId);
  const roles = rolesData?.data ?? [];

  const togglePerson = (id: number) =>
    setSelectedPeople(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleRole = (id: number) =>
    setSelectedRoles(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSend = () => {
    onSend([...selectedPeople], [...selectedRoles]);
    setSelectedPeople(new Set());
    setSelectedRoles(new Set());
    onClose();
  };

  const totalSelected = selectedPeople.size + selectedRoles.size;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "10px",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", maxHeight: "85vh" }}>

        {/* ── Header ── */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 3, pt: 2.5, pb: 2,
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "text.primary", letterSpacing: "0.02em" }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={onClose}
            sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── Tabs ── */}
        <Box sx={{ display: "flex", borderBottom: "1px solid", borderColor: "divider", flexShrink: 0 }}>
          {(["people", "roles"] as Tab[]).map(t => (
            <Box
              key={t}
              onClick={() => setTab(t)}
              sx={{
                flex: 1, py: 1.5, textAlign: "center", cursor: "pointer",
                borderBottom: "3px solid",
                borderColor: tab === t ? "primary.main" : "transparent",
                mb: "-1px",
                transition: "border-color 0.2s",
              }}
            >
              <Typography sx={{
                fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.1em",
                color: tab === t ? "primary.main" : "text.secondary",
                transition: "color 0.2s",
              }}>
                {t.toUpperCase()}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── List ── */}
        <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          <LoadingOverlay isLoading={isLoading || rolesLoading} />
          {!(isLoading || rolesLoading) && (<>
          <Divider sx={{ borderColor: "divider" }} />
          {tab === "people" ? (
            users.length > 0 ? [...users].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)).map((user) => {
              const checked = selectedPeople.has(user.user_id);
              return (
                <Box key={user.user_id}>
                  <Box
                    onClick={() => togglePerson(user.user_id)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 2,
                      px: 3, py: 2, cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                      transition: "background-color 0.12s",
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      onChange={() => {}}
                      sx={{
                        p: 0, color: "text.disabled",
                        "&.Mui-checked": { color: "primary.main" },
                        "& .MuiSvgIcon-root": { fontSize: "1.6rem" },
                      }}
                    />
                    <Typography sx={{ color: "text.primary", fontSize: "0.95rem", fontWeight: 400, letterSpacing: "0.02em" }}>
                      {user.first_name} {user.last_name}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: "divider" }} />
                </Box>
              );
            }) : (
              <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", mt: 6 }}>
                No people found.
              </Typography>
            )
          ) : roles.length > 0 ? (
            roles.map(role => (
              <RoleRow
                key={role.role_id}
                role={role}
                checked={selectedRoles.has(role.role_id)}
                onToggle={toggleRole}
              />
            ))
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", mt: 6 }}>
              No roles found.
            </Typography>
          )}
          </>)}
        </Box>

        {/* ── Footer ── */}
        <Box sx={{
          px: 3, py: 2, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
        }}>
          {showWarning ? (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flex: 1 }}>
              <WarningAmberIcon sx={{ color: "warning.main", fontSize: "1.1rem", mt: "2px", flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75rem", lineHeight: 1.5 }}>
                Please ensure the selected users have access to the file from The BPR app.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          <Button
            variant="contained"
            disabled={totalSelected === 0}
            onClick={handleSend}
            sx={{
              fontWeight: 700, px: 3, py: 1.3,
              borderRadius: "8px", fontSize: "0.82rem",
              letterSpacing: "0.06em", whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            {buttonLabel}
          </Button>
        </Box>

      </DialogContent>
    </Dialog>
  );
};

export default AnnounceContentDialog;